/**
 * First-party visitor analytics for the website.
 *
 * Posts to the same `POST /events/track/batch` the mobile app uses, so web and
 * app traffic land in one `user_sessions` / `session_events` pipeline and the
 * admin panel can compare them with a GROUP BY rather than a retrofit.
 *
 * Design notes, each of which is load-bearing:
 *
 *  - **Batched.** Events are buffered and flushed on a timer, at a size cap, or
 *    when the page is hidden. One request per page view would be both wasteful
 *    and, against the backend's per-IP limiter, actively harmful to the visitor.
 *  - **The buffer is cleared only after the server accepts it.** A failed flush
 *    keeps the events for the next attempt instead of dropping them.
 *  - **The buffer is persisted.** A visitor who closes the tab mid-session would
 *    otherwise lose everything since the last flush.
 *  - **Never throws, never blocks.** Analytics failing must never break a page.
 */
import axiosPublic from '@/api/axiosPublic';

const INSTALL_KEY = 'lk_install_id';
const QUEUE_KEY = 'lk_analytics_queue';
const SOURCE_KEY = 'lk_first_source';

const FLUSH_INTERVAL_MS = 10000;
const FLUSH_AT_COUNT = 10;
/** The server caps a batch at 50; stay under it so a flush is never rejected whole. */
const MAX_QUEUE = 45;

let queue = [];
let timer = null;
let userId = null;

/**
 * Local builds talk to the same API as the live site (UAT *is* production for
 * the website), so without this every dev and preview session was recorded as
 * a real visitor. Off on localhost; set `VITE_ANALYTICS_LOCAL=true` to exercise
 * the pipeline itself from a local build.
 */
const ENABLED = (() => {
  if (import.meta.env.VITE_ANALYTICS_LOCAL === 'true') return true;
  try {
    return !/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  } catch {
    return true;
  }
})();

const safeLocal = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private mode or blocked storage: tracking degrades to per-page-load
      // rather than breaking the site.
    }
  },
};

/** A stable per-browser id, the web equivalent of the app's install id. */
function installId() {
  let id = safeLocal.get(INSTALL_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    safeLocal.set(INSTALL_KEY, id);
  }
  return id;
}

/**
 * Where this visitor originally came from. First touch wins: the campaign that
 * earned the visit is more interesting than the last page they refreshed on.
 */
function firstSource() {
  const existing = safeLocal.get(SOURCE_KEY);
  if (existing) return existing;

  let source = null;
  try {
    const utm = new URLSearchParams(window.location.search).get('utm_source');
    if (utm) source = utm;
    else if (document.referrer) {
      const host = new URL(document.referrer).hostname;
      if (host && host !== window.location.hostname) source = host;
    }
  } catch {
    source = null;
  }

  source = source || 'direct';
  safeLocal.set(SOURCE_KEY, source);
  return source;
}

function loadQueue() {
  try {
    const raw = safeLocal.get(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE) : [];
  } catch {
    return [];
  }
}

function persistQueue() {
  safeLocal.set(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
}

async function flush({ keepalive = false } = {}) {
  if (!ENABLED || queue.length === 0) return;

  // Snapshot rather than clear: if the request fails these go back, so a
  // dropped connection costs nothing.
  const batch = queue.slice(0, MAX_QUEUE);

  const payload = {
    installId: installId(),
    deviceType: 'web',
    source: firstSource(),
    ...(userId ? { userId } : {}),
    events: batch.map((entry) => ({
      eventType: entry.eventType,
      metadata: entry.metadata,
    })),
  };

  try {
    if (keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // On page hide an XHR is usually cancelled mid-flight; sendBeacon is the
      // only thing the browser guarantees to deliver.
      const url = `${import.meta.env.VITE_API_BASE_URL}/events/track/batch`;
      const ok = navigator.sendBeacon(
        url,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
      if (ok) {
        queue = queue.slice(batch.length);
        persistQueue();
      }
      return;
    }

    await axiosPublic.post('/events/track/batch', payload);
    queue = queue.slice(batch.length);
    persistQueue();
  } catch {
    // Keep the events and try again on the next flush.
  }
}

function scheduleFlush() {
  if (timer) return;
  timer = setInterval(() => {
    flush();
  }, FLUSH_INTERVAL_MS);
}

/** Queue an event. Safe to call from anywhere, including render paths. */
export function track(eventType, metadata = {}) {
  if (!ENABLED) return;
  try {
    queue.push({ eventType, metadata });
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    persistQueue();
    if (queue.length >= FLUSH_AT_COUNT) flush();
  } catch {
    // Never let instrumentation break a page.
  }
}

/**
 * Attach the signed-in customer to this browser's events.
 *
 * Events recorded while they were a guest keep their null userId - the backend
 * links the *device* to the user, so the guest history stays reachable through
 * the device without rewriting history.
 */
export function identify(id) {
  userId = id ? Number(id) : null;
  if (userId) flush();
}

/** Called once at app start. */
export function initAnalytics() {
  if (!ENABLED) return;
  queue = loadQueue();
  firstSource();
  track('app_open', { path: window.location.pathname });
  scheduleFlush();

  // `visibilitychange` fires reliably on mobile where `beforeunload` does not.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush({ keepalive: true });
  });
  window.addEventListener('pagehide', () => flush({ keepalive: true }));
}
